import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf/dist/jspdf.umd.min';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from 'react-router-dom';
import { Button, Card, CardBody } from 'reactstrap';
import { toast } from 'react-toastify';
import PageHeader from '../common/PageHeader';
import apiBase from '../../apiConfig';
import {
  buildAnalysisDocument,
  buildAnalysisDocumentUrl,
  buildAnalysisQrDataUrl,
  createAnalysisDocumentKey,
  fetchAssetAsDataUrl,
  resolveFilialLogoSrc,
  toFileSafeName
} from './analysisDocumentUtils';

const createDraftFromItem = item => ({
  result: item.result || '',
  status: item.status_key || 'pending',
  template_id: item.template_id ? String(item.template_id) : '',
  method_id: item.method_id ? String(item.method_id) : '',
  norm_ids: Array.isArray(item.norm_ids) ? item.norm_ids.map(id => Number(id)) : []
});

const statusLabels = {
  pending: 'Kutilmoqda',
  in_progress: 'Jarayonda',
  ready: 'Tayyor',
  printed: 'Chop etilgan'
};

const AnalysisDocumentPage = () => {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const patientId = Number(searchParams.get('patient_id') || 0);
  const analysisId = Number(searchParams.get('analysis_id') || 0);
  const key = String(searchParams.get('key') || '');
  const [patient, setPatient] = useState(null);
  const [analysisItem, setAnalysisItem] = useState(null);
  const [filial, setFilial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrSrc, setQrSrc] = useState('');

  const isKeyValid = createAnalysisDocumentKey(patientId, analysisId) === key;
  const documentUrl = buildAnalysisDocumentUrl(patientId, analysisId, window.location.origin);

  useEffect(() => {
    if (!patientId || !analysisId || !isKeyValid) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);

      try {
        const [patientRes, analysisRes, filialRes] = await Promise.all([
          axios.get(`${apiBase}/patients.php`, { params: { id: patientId } }),
          axios.get(`${apiBase}/laboratory.php`, { params: { patient_id: patientId, limit: 500 } }),
          axios.get(`${apiBase}/filials.php`)
        ]);

        const patientData = patientRes.data?.patient || null;
        const analysisData = (analysisRes.data?.control || []).find(entry => Number(entry.id) === analysisId) || null;
        const filialData = (filialRes.data?.filials || []).find(
          entry => Number(entry.id) === Number(patientData?.filial_id || 0)
        ) || null;

        if (!patientData || !analysisData) {
          throw new Error("Hujjat ma'lumotlari topilmadi");
        }

        setPatient(patientData);
        setAnalysisItem(analysisData);
        setFilial(filialData);
      } catch (error) {
        toast.error(error.response?.data?.message || error.message || "Hujjat ma'lumotlari yuklanmadi");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [analysisId, isKeyValid, patientId]);

  useEffect(() => {
    if (!documentUrl) {
      setQrSrc('');
      return;
    }

    buildAnalysisQrDataUrl(documentUrl)
      .then(setQrSrc)
      .catch(() => setQrSrc(''));
  }, [documentUrl]);

  const handlePdfDownload = async () => {
    if (!patient || !analysisItem) {
      return;
    }

    try {
      const draft = createDraftFromItem(analysisItem);
      const qrSrc = await buildAnalysisQrDataUrl(documentUrl);
      let exportFilial = filial;

      if (filial?.logo) {
        try {
          const logoDataUrl = await fetchAssetAsDataUrl(resolveFilialLogoSrc(filial.logo));
          exportFilial = { ...filial, logo: logoDataUrl || filial.logo };
        } catch (error) {
          exportFilial = { ...filial };
        }
      }

      const payload = buildAnalysisDocument({
        patient,
        item: analysisItem,
        draft,
        filial: exportFilial,
        statusLabel: statusLabels[draft.status] || statusLabels.pending,
        documentUrl,
        qrSrc,
        specialistName: analysisItem.specialist_name || '',
        doctorName: patient.doctor || ''
      });
      const mountNode = document.createElement('div');
      mountNode.style.position = 'fixed';
      mountNode.style.left = '-100000px';
      mountNode.style.top = '0';
      mountNode.style.width = '210mm';
      mountNode.innerHTML = `<style>${payload.styles}</style>${payload.markup}`;
      document.body.appendChild(mountNode);

      try {
        const target = mountNode.querySelector('.med-doc-page');
        if (!target) {
          throw new Error('PDF tayyorlash uchun hujjat topilmadi');
        }

        const canvas = await html2canvas(target, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          windowWidth: target.scrollWidth,
          windowHeight: target.scrollHeight
        });

        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/png', 1.0);
        const pageWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * pageWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage('a4', 'p');
          pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
          heightLeft -= pageHeight;
        }

        pdf.save(`${toFileSafeName(patient.full_name)}-${toFileSafeName(analysisItem.analysis_title)}.pdf`);
      } finally {
        document.body.removeChild(mountNode);
      }
    } catch (error) {
      toast.error(error.message || 'PDF yuklab olinmadi');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!patientId || !analysisId || !isKeyValid) {
    return (
      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="text-center py-5">Hujjat kaliti noto'g'ri yoki to'liq emas.</CardBody>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="text-center py-5">Hujjat yuklanmoqda...</CardBody>
      </Card>
    );
  }

  if (!patient || !analysisItem) {
    return (
      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="text-center py-5">Hujjat ma'lumotlari topilmadi.</CardBody>
      </Card>
    );
  }

  const draft = createDraftFromItem(analysisItem);
  const payload = buildAnalysisDocument({
    patient,
    item: analysisItem,
    draft,
    filial,
    statusLabel: statusLabels[draft.status] || statusLabels.pending,
    documentUrl,
    qrSrc,
    specialistName: analysisItem.specialist_name || '',
    doctorName: patient.doctor || ''
  });

  return (
    <>
      <PageHeader
        title="Analiz Hujjati"
        description="Analiz natijasining tasdiqlangan hujjati."
        className="mb-3"
      />
      <Card className="border-0 shadow-sm med-dashboard-card mb-3">
        <CardBody className="d-flex justify-content-end">
          <Button color="light" className="mr-2" onClick={handlePrint}>
            <FontAwesomeIcon icon="print" className="mr-2" />
            Chop etish
          </Button>
          <Button color="primary" onClick={handlePdfDownload}>
            <FontAwesomeIcon icon="file-pdf" className="mr-2" />
            PDF yuklab olish
          </Button>
        </CardBody>
      </Card>
      <div dangerouslySetInnerHTML={{ __html: `<style>${payload.styles}</style>${payload.markup}` }} />
    </>
  );
};

export default AnalysisDocumentPage;
